import SearchBar from '@components/lib/searchbar/searchbar';
import {
  faPlus,
  faChevronDown,
  faPeopleGroup,
} from '@fortawesome/pro-regular-svg-icons';
import { faX } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTeamListQuery } from '@gen2/api/team/hooks';
import { useTeamMemberListQuery } from '@gen2/api/team-member/hooks';
import {
  useGetWatchersQuery,
  useUnwatchMutation,
  useWatchMutation,
  WatcherKeys,
} from '@gen2/api/watchers/hooks';
import { queryClient } from '@gen2/config';
import { useAuth, useToast } from '@gen2/hooks';
import { PermissionsKeys } from '@gen2/types/permissions';
import { TTeam } from '@gen2/types/team';
import { TUser } from '@gen2/types/user';
import {
  Permissions,
  usePermissions,
} from '@gen2/utils/permissions/permissions';
import { Box, Chip, Divider, IconButton, Tooltip } from '@mui/material';
import { ChangeEvent, useMemo, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import {
  StyledBox,
  StyledWatchers,
  TeamsIcon,
  WatchersAvatar,
  WatchersAvatarGroup,
  WatchersField,
  WatchersLabel,
  WatchersMenu,
  WatchersMenuAction,
  WatchersMenuHeading,
  WatchersMenuItem,
  WatchersMenuItemButton,
} from './watchers.styled';

export interface IWatchersProps {
  inviteId: string;
}

const Watchers = ({ inviteId }: IWatchersProps) => {
  const anchorEl = useRef<HTMLButtonElement>(null);

  const { mutate: watchMutation, isLoading: isWatching } = useWatchMutation();
  const { mutate: unwatchMutation, isLoading: isUnwatching } =
    useUnwatchMutation();
  const { data } = useGetWatchersQuery(inviteId);
  const { user: userMe } = useAuth();
  const { withPermissions } = usePermissions();
  const toast = useToast();

  // Check team permissions
  const hasListWatcherPermission = withPermissions(
    PermissionsKeys.LIST_WATCHER,
  );
  const [isWatchersOpen, setIsWatchersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedValue] = useDebounceValue(searchQuery, 300);
  const isWatchersNotEmpty = useMemo(() => !!data?.watchers?.length, [data]);

  const { data: teamMembers } = useTeamMemberListQuery({
    'sort[column]': 'first_name',
    'sort[order]': 'asc',
    'filter[permission]': [PermissionsKeys.WATCH_INVITE],
    per_page: 10,
    page: 1,
    'filter[search]': debouncedValue,
    'filter[active]': 1,
  });

  // teams query
  const { data: teams } = useTeamListQuery({
    'sort[column]': 'name',
    'sort[order]': 'asc',
    per_page: 10,
    page: 1,
    'filter[search]': debouncedValue,
  });

  const displayedUsers = useMemo(() => {
    if (!debouncedValue) {
      return data?.watchers.map(({ user }) => user) || [];
    }

    const watcherUsers = data?.watchers.map(({ user }) => user) || [];
    const searchUsers = teamMembers?.data || [];

    const allUsers = [...watcherUsers];
    searchUsers.forEach((searchUser) => {
      if (!allUsers.some((user) => user.id === searchUser.id)) {
        allUsers.push(searchUser);
      }
    });

    return allUsers;
  }, [data?.watchers, teamMembers?.data, debouncedValue]);

  const onWatch = (
    userIds: string[] = [userMe?.id ?? ''],
    teamIds: string[] = [],
  ) => {
    watchMutation(
      {
        inviteId: inviteId ?? '',
        user_ids: userIds,
        team_ids: teamIds,
      },
      {
        onSuccess: async () => {
          toast.show({
            variant: 'success',
            text: 'You are now watching this invite',
          });

          await queryClient.invalidateQueries([WatcherKeys.getWatchers]);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (err: any) => {
          toast.show({
            variant: 'error',
            text: err?.data.message,
          });
        },
      },
    );
  };

  const onUnwatch = (user_id?: string) => {
    unwatchMutation(
      {
        inviteId: inviteId ?? '',
        user_id: user_id ?? '',
      },
      {
        onSuccess: async () => {
          toast.show({
            variant: 'success',
            text: 'You are no longer watching this invite',
          });

          await queryClient.invalidateQueries([WatcherKeys.getWatchers]);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (err: any) => {
          toast.show({
            variant: 'error',
            text: err?.data.message,
          });
        },
      },
    );
  };

  const handleRemoveWatcher = (user: TUser) => {
    onUnwatch(user.id);
    setIsWatchersOpen(false);
  };

  const onWatchAction = () => {
    data?.is_watching ? onUnwatch() : onWatch();
    setIsWatchersOpen(false);
  };

  const handleUserSearch = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSearchQuery(event.target.value);
  };

  const handleUserReset = () => {
    setSearchQuery('');
  };

  const handleOnWatch = (user: TUser) => {
    const alreadyWatchingUserIds = data?.watchers.map((w) => w.user.id) || [];

    if (alreadyWatchingUserIds.includes(user.id)) {
      toast.show({
        variant: 'info',
        text: `${user.first_name} is already a watcher`,
      });
      return;
    }

    onWatch([user.id], []);
  };

  const handleOnWatchTeam = (team: TTeam) => {
    onWatch([], [team.id]);
  };

  if (!hasListWatcherPermission) {
    return null;
  }

  return (
    <StyledWatchers>
      <WatchersLabel>Watchers:</WatchersLabel>
      <WatchersField>
        {!isWatchersNotEmpty ? (
          <Permissions allow={PermissionsKeys.WATCH_INVITE}>
            <IconButton
              color="tonalLight"
              size="small"
              ref={anchorEl}
              onClick={() => onWatch()}
              disabled={isWatching}
            >
              <FontAwesomeIcon icon={faPlus} size="sm" />
            </IconButton>
          </Permissions>
        ) : (
          <WatchersAvatarGroup
            spacing="medium"
            max={9}
            total={data?.watchers.length}
            slotProps={{
              additionalAvatar: {
                className: 'surplus',
              },
            }}
          >
            {data &&
              data.watchers.map(({ id, user }) => (
                <Tooltip
                  key={id}
                  title={user.first_name + ' ' + user.last_name}
                  placement="top"
                >
                  <WatchersAvatar
                    alt={user.first_name + ' ' + user.last_name}
                    src={user.avatar_url}
                  >
                    {user.first_name[0] + user.last_name[0]}
                  </WatchersAvatar>
                </Tooltip>
              ))}
          </WatchersAvatarGroup>
        )}
        {isWatchersNotEmpty && (
          <IconButton
            color="tonalLight"
            size="small"
            ref={anchorEl}
            onClick={() => {
              setIsWatchersOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faChevronDown} size="sm" />
          </IconButton>
        )}
      </WatchersField>
      <WatchersMenu
        open={isWatchersOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => {
          setIsWatchersOpen(false);
          setSearchQuery('');
        }}
      >
        <Permissions allow={PermissionsKeys.WATCH_INVITE}>
          <WatchersMenuItemButton
            onClick={onWatchAction}
            disabled={isWatching || isUnwatching}
          >
            <WatchersMenuAction>
              <FontAwesomeIcon
                icon={data?.is_watching ? faX : faPlus}
                size="sm"
              />
              {data?.is_watching ? 'Stop Watching' : 'Start Watching'}
            </WatchersMenuAction>
          </WatchersMenuItemButton>
        </Permissions>
        <Box sx={{ py: 1 }}>
          <SearchBar
            onChange={handleUserSearch}
            value={searchQuery}
            onReset={handleUserReset}
            placeholder="Search team members or teams"
            id="search-bar"
            autoFocus
            mode="dark"
            $isVariant={false}
          />
        </Box>
        <WatchersMenuHeading>INVITE WATCHERS</WatchersMenuHeading>
        {/* users */}
        {displayedUsers.map((user) => {
          const isAlreadyWatching = data?.watchers.some(
            (w) => w.user.id === user.id,
          );
          return (
            <Box
              key={user.id}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <WatchersMenuItem
                key={user.id}
                onClick={() => handleOnWatch(user)}
              >
                <WatchersAvatar
                  alt={user.first_name + ' ' + user.last_name}
                  src={user.avatar_url}
                >
                  {user.first_name[0] + user.last_name[0]}
                </WatchersAvatar>
                <StyledBox>
                  <Box>
                    {user.first_name + ' ' + user.last_name}
                    {userMe?.id === user.id && ' (Myself)'}
                  </Box>
                  {isAlreadyWatching && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip label="Watcher" size="small" color="contact" />
                    </Box>
                  )}
                </StyledBox>
              </WatchersMenuItem>
              {isAlreadyWatching && (
                <IconButton
                  size="small"
                  disabled={isUnwatching}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveWatcher(user);
                  }}
                >
                  <FontAwesomeIcon icon={faX} size="sm" />
                </IconButton>
              )}
            </Box>
          );
        })}
        <Divider />
        {/* teams */}
        {teams?.data?.length && teams?.data?.length > 0 ? (
          teams?.data?.map((team: TTeam) => (
            <WatchersMenuItem
              key={team.id}
              onClick={() => handleOnWatchTeam(team)}
            >
              <TeamsIcon>
                <FontAwesomeIcon icon={faPeopleGroup} size="lg" />
              </TeamsIcon>
              <StyledBox>
                <Box>{team.name}</Box>
              </StyledBox>
            </WatchersMenuItem>
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 2 }}>No teams found</Box>
        )}
      </WatchersMenu>
    </StyledWatchers>
  );
};

export default Watchers;
